import { useState, useEffect } from 'react'
import { ScoopDialog, ScoopButton, ScoopTextField, ScoopText, ScoopColumn } from '../../../../ui'
import { FormField } from '../forms/shared'
import styles from './CreateContextModal.module.scss'

/**
 * Converts a display name to snake_case
 * @param {string} displayName - The display name to convert
 * @returns {string} - The snake_case version
 */
const toSnakeCase = (displayName) => {
    return displayName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Remove consecutive underscores
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
}

/**
 * CreateContextModal - Modal for creating a new investigation context
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onCreate - Callback when context is created
 * @param {boolean} isCreating - Whether creation is in progress
 * @param {Array} existingContexts - List of existing context names to prevent duplicates
 */
export const CreateContextModal = ({
    open,
    onClose,
    onCreate,
    isCreating = false,
    existingContexts = [],
}) => {
    const [formData, setFormData] = useState({
        displayName: '',
        contextName: '',
        purpose: '',
        description: '',
    })
    const [errors, setErrors] = useState({})
    const [manualNameEdit, setManualNameEdit] = useState(false)

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            setFormData({
                displayName: '',
                contextName: '',
                purpose: '',
                description: '',
            })
            setErrors({})
            setManualNameEdit(false)
        }
    }, [open])

    const handleDisplayNameChange = (e) => {
        const value = e.target.value
        const newContextName = manualNameEdit ? formData.contextName : toSnakeCase(value)

        setFormData((prev) => ({
            ...prev,
            displayName: value,
            contextName: newContextName,
        }))

        // Clear errors
        setErrors((prev) => ({ ...prev, displayName: null, contextName: null }))
    }

    const handleContextNameChange = (e) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
        setManualNameEdit(true)
        setFormData((prev) => ({
            ...prev,
            contextName: value,
        }))
        setErrors((prev) => ({ ...prev, contextName: null }))
    }

    const handlePurposeChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            purpose: e.target.value,
        }))
        setErrors((prev) => ({ ...prev, purpose: null }))
    }

    const handleDescriptionChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            description: e.target.value,
        }))
    }

    const validate = () => {
        const newErrors = {}

        if (!formData.displayName.trim()) {
            newErrors.displayName = 'Display name is required'
        }

        if (!formData.contextName.trim()) {
            newErrors.contextName = 'Context name is required'
        } else if (!/^[a-z][a-z0-9_]*$/.test(formData.contextName)) {
            newErrors.contextName =
                'Must start with a letter and contain only lowercase letters, numbers, and underscores'
        } else if (existingContexts.includes(formData.contextName)) {
            newErrors.contextName = 'A context with this name already exists'
        }

        if (!formData.purpose.trim()) {
            newErrors.purpose = 'Purpose is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleCreate = () => {
        if (!validate()) return

        // Create the context template
        const contextData = {
            name: formData.contextName,
            metadata: {
                display_name: formData.displayName.trim(),
                purpose: formData.purpose.trim(),
                description: formData.description.trim(),
                key_metrics: [],
                applicable_datasets: [],
            },
            local_definitions: {},
            probes: [],
            patterns: {},
            spawn_guidance: [],
            synthesis: {
                model_preference: 'balanced',
            },
        }

        onCreate(contextData)
    }

    const isValid =
        formData.displayName.trim() && formData.contextName.trim() && formData.purpose.trim()

    return (
        <ScoopDialog
            open={open}
            onClose={onClose}
            title="Create New Context"
            maxWidth="sm"
            actions={
                <>
                    <ScoopButton variant="grey" onClick={onClose} disabled={isCreating}>
                        Cancel
                    </ScoopButton>
                    <ScoopButton
                        variant="default"
                        onClick={handleCreate}
                        disabled={!isValid || isCreating}
                        loading={isCreating}
                    >
                        Create Context
                    </ScoopButton>
                </>
            }
        >
            <div className={styles.content}>
                <ScoopText className={styles.description}>
                    Create a new investigation context. The context name will be used as the file
                    identifier and should be unique.
                </ScoopText>

                <ScoopColumn className={styles.form}>
                    <FormField
                        label="Display Name"
                        helpText="User-friendly name shown in the UI"
                        required
                        error={errors.displayName}
                    >
                        <ScoopTextField
                            value={formData.displayName}
                            onChange={handleDisplayNameChange}
                            placeholder="e.g., Death Spiral Analysis"
                            disabled={isCreating}
                            autoFocus
                        />
                    </FormField>

                    <FormField
                        label="Context Name"
                        helpText="Technical identifier (auto-generated from display name)"
                        required
                        error={errors.contextName}
                    >
                        <ScoopTextField
                            value={formData.contextName}
                            onChange={handleContextNameChange}
                            placeholder="e.g., death_spiral_analysis"
                            disabled={isCreating}
                            className={styles.codeInput}
                        />
                    </FormField>

                    <FormField
                        label="Purpose"
                        helpText="Brief description of what this context investigates"
                        required
                        error={errors.purpose}
                    >
                        <ScoopTextField
                            value={formData.purpose}
                            onChange={handlePurposeChange}
                            placeholder="e.g., Detect declining loan portfolio patterns"
                            multiline
                            rows={2}
                            disabled={isCreating}
                        />
                    </FormField>

                    <FormField label="Description" helpText="Detailed description (optional)">
                        <ScoopTextField
                            value={formData.description}
                            onChange={handleDescriptionChange}
                            placeholder="Provide additional details about this context..."
                            multiline
                            rows={3}
                            disabled={isCreating}
                        />
                    </FormField>
                </ScoopColumn>
            </div>
        </ScoopDialog>
    )
}
